<template>
  <div class="setup-fee-emit-form" data-testid="setup-fee-emit-form">
    <form @submit.prevent="handleSubmit">
      <div class="form-fields">
        <franchise-select
          v-model="formData.franquia_id"
          elem-id="setup-fee-emit-franchise"
          :label="t('Franquia')"
          data-testid="setup-fee-emit-franchise-select"
          @franchise-data="onFranchiseData"
        />
        <select-list
          v-model="formData.setup_price_default_id"
          elem-id="setup-fee-emit-provider"
          :label="t('Mercado')"
          :itens-select="marketOptions"
          :auto-select-first="true"
          :ng-required="true"
          data-testid="setup-fee-emit-provider-select"
        />
        <!--
          TWO empty states, deliberately distinct: they have different fixes.
          "country unknown" is a franchise-data problem (permissions scope or a
          stale cache); "no market" is a configuration problem (markets page +
          gateway toggle). Collapsing them sends the operator to the wrong page.
        -->
        <div
          v-if="countryUnknown"
          class="no-gateway-hint"
          data-testid="setup-fee-emit-country-unknown"
        >
          {{ t('Selecione a franquia. Se já selecionou, não foi possível determinar o país dela — verifique o cadastro da franquia.') }}
        </div>
        <div
          v-else-if="noGatewayAvailable"
          class="no-gateway-hint"
          data-testid="setup-fee-emit-no-gateway"
        >
          {{ t('Nenhum gateway habilitado para taxa de implantação neste mercado. Configure os mercados e o toggle na página de gateways.') }}
        </div>
        <!-- THE price, read-only: it belongs to the market RECORD selected
             above — the same record the backend prices from. A typed field was
             both a lie (rendered in the operator's own currency) and a loophole
             (the API took any amount). -->
        <div class="market-price" data-testid="setup-fee-emit-market-price">
          <span class="market-price-label">{{ t('Valor da taxa') }}</span>
          <span v-if="marketPriceDisplay" class="market-price-value" data-testid="setup-fee-emit-market-price-value">
            {{ marketPriceDisplay }}
            <small v-if="marketCurrency">{{ marketOriginLabel }}</small>
          </span>
          <span v-else class="market-price-empty" data-testid="setup-fee-emit-market-price-empty">
            {{ t('Selecione a franquia e o mercado para resolver o preço') }}
          </span>
        </div>
        <input-number
          v-model="formData.installments"
          elem-id="setup-fee-emit-installments"
          :label="t('Parcelas')"
          data-testid="setup-fee-emit-installments-input"
          :min="1"
          :max="maxInstallmentsCap"
          :ng-required="true"
        />

      </div>
      <div v-if="errorMessage" class="error-message" data-testid="setup-fee-emit-error">
        {{ errorMessage }}
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSetupFee } from '../../../composables/useSetupFee'
import { usePaymentGatewayRegistry } from '../../../composables/usePaymentGatewayRegistry'
import { emitValidationFailed } from '../../../composables/useFormValidation'
import { log } from '../../../utils/DebugLogger'
import { currencyFactor } from '../../../utils/currencyMinorUnits'
import FranchiseSelect from '../../../inputs/FranchiseSelect.vue'
import InputNumber from '../../../inputs/InputNumber.vue'
import SelectList from '../../../inputs/SelectList.vue'
import type { SetupFee_EmitResponseDados } from '../../../contracts/SetupFee_EmitResponse'
import type { FranquiaData } from '../../../services/permissionsService'

const { t, n } = useI18n()
const { emit: emitSetupFee, fetchDefaults, defaultsList } = useSetupFee()
const { featureMap, loadMap } = usePaymentGatewayRegistry()

const props = defineProps<{
  initialFranquiaId?: number | null
  initialMarketId?: number | null
  initialInstallments?: number | null
}>()

const emit = defineEmits<{
  (e: 'saved', payload: SetupFee_EmitResponseDados): void
  (e: 'validation-failed'): void
}>()

const errorMessage = ref<string>('')

const formData = reactive({
  franquia_id: props.initialFranquiaId ?? null as number | string | null,
  setup_price_default_id: props.initialMarketId ?? (undefined as number | undefined),
  installments: props.initialInstallments ?? 1,
})

/**
 * The chosen franchise's ISO2 country, from FranchiseSelect's franchise-data
 * event (Pais). Null until known — every active record is offered until then;
 * the backend re-checks the country against the chosen record either way.
 */
const franchiseCountry = ref<string | null>(null)

function onFranchiseData(data: FranquiaData | null | undefined): void {
  // PaisISO is the server-normalised ISO2; Pais is FREE TEXT ('Brasil', 'BR',
  // 'Chile') and is spelled out for most franchises.
  //
  // This used to read `Pais` alone and test it against /^[A-Z]{2}$/, so every
  // franchise whose registry row spells the country out — which is most of
  // them, the pais_to_iso migration having only ever run for some — answered
  // "country unknown", offered NO markets, and told staff to "verifique o
  // cadastro da franquia" about a row that was fine.
  //
  // The raw fallback stays for a permissions payload cached before PaisISO
  // existed (2 h TTL): an already-ISO value is still correct, and a spelled-out
  // one degrades to the same "unknown" as before rather than to a wrong guess.
  const iso = String(data?.Dados?.PaisISO ?? '').toUpperCase()
  if (/^[A-Z]{2}$/.test(iso)) {
    franchiseCountry.value = iso
    return
  }
  const pais = String(data?.Dados?.Pais ?? '').toUpperCase()
  franchiseCountry.value = /^[A-Z]{2}$/.test(pais) ? pais : null
}

/**
 * One market record's price, in ITS OWN currency.
 *
 * currencyFactor, never a hardcoded /100: CLP and JPY are zero-decimal, so
 * dividing by 100 would show a Chilean market price 100x too small. The
 * try/catch mirrors BillingThreeSixtyDetail — an unrecognized currency code
 * makes Intl throw, and a throw inside a computed takes the form down with it.
 */
function formatMarketPrice(row: { amount_cents: number; currency: string }): string {
  const major = row.amount_cents / currencyFactor('pt-BR', row.currency)
  try {
    return n(major, { style: 'currency', currency: row.currency })
  } catch {
    return `${row.currency} ${major}`
  }
}

/**
 * Market rows eligible for THIS emission: active, and serving the franchise's
 * country. Mirrors the backend's findActiveByCountry so the form offers exactly
 * the records the charge path will accept.
 *
 * FAILS CLOSED on an unknown country. This used to offer EVERY active record
 * until the country was known, on the reasoning that "the backend re-checks the
 * country against the chosen record either way". It does — and refuses, with
 * `mercado_nao_atende_pais`. So the form was presenting markets the charge path
 * was guaranteed to reject, and a Chilean franchise was shown BRL and USD.
 *
 * That mirrors the backend's own D6 decision, which deliberately removed the
 * silent BR fallback so an unresolvable country yields a NAMED refusal instead
 * of quietly shopping the wrong catalog. A picker that offers what cannot be
 * bought is the same mistake wearing a different hat.
 */
const marketRows = computed(() => {
  if (franchiseCountry.value === null) return []

  return defaultsList.value.filter((d) => {
    if (d.active === false) return false
    return (d.countries ?? []).includes(franchiseCountry.value as string)
  })
})

/**
 * The MARKET RECORDS on offer = rows serving this country ∩ admin toggle
 * ('setup_fee' feature). The option VALUE is the record id, so the row staff
 * see priced is the exact row the backend charges from — picking a provider
 * and letting each side re-resolve the row was two lookups for one question
 * (2026-08-20). Label carries the price so two records of the same gateway
 * are never ambiguous.
 *
 * NO fallback list: an empty set renders an honest empty state instead of
 * offering gateways the backend is guaranteed to refuse.
 */
const marketOptions = computed(() =>
  marketRows.value
    .filter((def) => featureMap.value?.['setup_fee']?.[def.provider_code]?.enabled === true)
    .map((def) => ({
      ID: Number(def.id ?? 0),
      Name: `${def.provider_code.charAt(0).toUpperCase()}${def.provider_code.slice(1)} — ${formatMarketPrice(def)}`,
    })),
)

const dataLoaded = ref(false)

/**
 * The franchise's country is not known — either none is selected yet, or the
 * selected one did not resolve (out of permissions scope, or a stale cache).
 *
 * Deliberately NOT gated on franquia_id being set. The market list is empty for
 * the same reason in both cases, and gating made the state unreachable at mount
 * — leaving the OTHER empty state ("no gateway for this market") to explain an
 * absence that has nothing to do with gateways, sending the operator to the
 * wrong page.
 */
const countryUnknown = computed(
  () => dataLoaded.value && franchiseCountry.value === null,
)

const noGatewayAvailable = computed(
  () => dataLoaded.value && !countryUnknown.value && marketOptions.value.length === 0,
)

const selectedDefault = computed(() => {
  const id = Number(formData.setup_price_default_id)
  if (!id) return null
  return marketRows.value.find((d) => Number(d.id) === id) ?? null
})

/**
 * The installment cap for THIS franchise on the selected market record.
 *
 * Read from `max_installments_by_country`, which the backend ships already
 * narrowed by the gateway's own rule — ASAAS forbids parcelamento for a foreign
 * customer, so the BRL/asaas record that offers Brazil 12× offers Chile and
 * Ecuador exactly 1. The row-level `max_installments` is the COMMERCIAL cap and
 * is a single number for every country the record serves, so it cannot answer
 * this on its own.
 *
 * Deliberately NOT re-derived here: a second copy of the provider's rules in
 * the UI would eventually drift, and the drift is silent — the form would offer
 * an installment count the charge path then refuses with
 * `installments_acima_do_cap`, a refusal that reads like a bug precisely
 * because the form said the number was allowed.
 *
 * Falls back to the row cap when the country is unknown or absent from the map
 * (an older backend). That is the pre-existing behaviour, and it fails toward
 * the SERVER's judgement: emit() re-checks and refuses by name.
 */
const maxInstallmentsCap = computed(() => {
  const row = selectedDefault.value
  if (!row) return 12

  const country = franchiseCountry.value
  const byCountry = row.max_installments_by_country
  if (country && byCountry && typeof byCountry[country] === 'number') {
    return byCountry[country]
  }

  return row.max_installments ?? 12
})

/**
 * The selected market record's price, shown read-only in ITS OWN currency —
 * the number the backend will actually charge. Empty until a record is
 * selected; the form refuses to submit in that state rather than emitting at
 * a price nobody can see.
 */
const marketCurrency = computed(() => selectedDefault.value?.currency ?? '')

const marketPriceDisplay = computed(() => {
  const row = selectedDefault.value
  if (!row || !row.currency) return ''
  return formatMarketPrice(row)
})

const marketOriginLabel = computed(() => {
  const row = selectedDefault.value
  if (!row) return ''
  const countries = (row.countries ?? []).join(', ')
  return countries ? `(${t('mercado')} ${countries})` : `(${t('mercado')} ${row.currency})`
})

// Re-clamp on the CAP, not on the market record alone: the cap also moves when
// the franchise changes, because it depends on that franchise's country. Watching
// only setup_price_default_id left a stale 12 in the field after switching from a
// Brazilian franchise to a Chilean one on the same market record.
watch(maxInstallmentsCap, (cap) => {
  if (formData.installments > cap) {
    formData.installments = cap
  }
})

/**
 * Turn the backend's refusal into something a staff member can act on.
 *
 * Two shapes arrive here:
 *
 *   `gateway_error: <prose>` — the provider adapter already wrote a full
 *      sentence naming the franchise and the fix. The prefix is machine
 *      routing, not information, so it is stripped and the prose shown as-is.
 *      Rewriting it would be strictly worse: the adapter knows things this form
 *      does not (which document, which country, which account permission).
 *
 *   a bare snake_case code — a domain refusal. Mapped where a plain-language
 *      version genuinely helps; anything unmapped falls through VERBATIM rather
 *      than becoming "erro ao emitir", because an unknown code is still a
 *      searchable fact and a generic message is not.
 */
function humanizeEmitError(raw: string | undefined): string {
  const message = (raw ?? '').trim()
  if (message === '') return t('Erro ao emitir taxa de implantação')

  if (message.startsWith('gateway_error:')) {
    const prose = message.slice('gateway_error:'.length).trim()
    return prose !== '' ? prose : t('O gateway recusou a cobrança')
  }

  const known: Record<string, string> = {
    pais_sem_mercado: t('A franquia está em um país sem mercado de cobrança configurado'),
    mercado_nao_atende_pais: t('O mercado escolhido não atende o país desta franquia'),
    mercado_nao_encontrado: t('Mercado não encontrado'),
    mercado_inativo: t('O mercado escolhido está inativo'),
    mercado_sem_preco_para_gateway: t('O mercado escolhido não tem preço configurado'),
    gateway_desabilitado_para_setup_fee: t('O gateway está desabilitado para taxa de implantação'),
    gateway_nao_registrado: t('O gateway do mercado escolhido não está registrado'),
    gateway_nao_suporta_moeda: t('O gateway não cobra na moeda deste mercado'),
    installments_acima_do_cap: t('Parcelas acima do máximo permitido para este mercado e país'),
    setup_ja_satisfeito: t('Esta franquia já tem a taxa de implantação quitada ou isenta'),
    cobranca_pendente_existe: t('Já existe uma cobrança pendente para esta franquia'),
  }

  return known[message] ?? message
}

function validateForm(): boolean {
  errorMessage.value = ''
  if (!formData.franquia_id) {
    errorMessage.value = t('Selecione uma franquia')
    emitValidationFailed('SetupFeeEmitForm', { franquia_id: errorMessage.value })
    return false
  }
  if (!formData.setup_price_default_id) {
    errorMessage.value = t('Selecione o mercado')
    emitValidationFailed('SetupFeeEmitForm', { setup_price_default_id: errorMessage.value })
    return false
  }
  // No resolved market row = no price. The backend refuses this with
  // mercado_sem_preco_para_gateway; refusing here keeps the operator from
  // submitting a charge whose amount nothing on screen could show.
  if (!selectedDefault.value || selectedDefault.value.amount_cents <= 0) {
    errorMessage.value = t('Nenhum preço de mercado para esta franquia e provedor')
    emitValidationFailed('SetupFeeEmitForm', { amount: errorMessage.value })
    return false
  }
  if (!formData.installments || formData.installments < 1) {
    errorMessage.value = t('Informe a quantidade de parcelas')
    emitValidationFailed('SetupFeeEmitForm', { installments: errorMessage.value })
    return false
  }
  if (formData.installments > maxInstallmentsCap.value) {
    // Number concatenated OUTSIDE t() — a {max} placeholder inside an
    // API-driven key renders literally until the key is translated.
    errorMessage.value = `${t('Parcelas não podem exceder')} ${maxInstallmentsCap.value}`
    emitValidationFailed('SetupFeeEmitForm', { installments: errorMessage.value })
    return false
  }
  return true
}

async function handleSubmit(): Promise<boolean> {
  if (!validateForm()) {
    emit('validation-failed')
    return false
  }

  // No amount travels: the backend prices from the market row itself.
  log('SetupFeeEmitForm', 'handleSubmit', 'Submitting setup fee charge', {
    franquia_id: formData.franquia_id,
    setup_price_default_id: formData.setup_price_default_id,
  })

  try {
    const result = await emitSetupFee({
      franquia_id: Number(formData.franquia_id),
      setup_price_default_id: Number(formData.setup_price_default_id),
      installments: Number(formData.installments),
    })
    emit('saved', result)
    return true
  } catch (err) {
    errorMessage.value = humanizeEmitError((err as Error)?.message)
    log('SetupFeeEmitForm', 'handleSubmit', 'Error emitting setup fee', {
      error: (err as Error)?.message,
    })
    return false
  }
}

watch(
  () => props.initialFranquiaId,
  (val) => {
    if (val != null) formData.franquia_id = val
  },
)

onMounted(async () => {
  try {
    await Promise.all([
      loadMap(),
      defaultsList.value.length === 0 ? fetchDefaults() : Promise.resolve([]),
    ])
  } catch {
    // Non-fatal: the empty state explains itself and the backend re-validates.
  } finally {
    dataLoaded.value = true
  }
})

defineExpose({
  handleSubmit,
  validateForm,
})
</script>


<style scoped>
.setup-fee-emit-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}
.market-price {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}
.market-price-label {
  font-size: 0.875rem;
  color: var(--p-text-muted-color, #6b7280);
}
.market-price-value {
  font-size: 1.125rem;
  font-weight: 600;
}
.market-price-value small {
  font-weight: 400;
  font-size: 0.8125rem;
  color: var(--p-text-muted-color, #6b7280);
  margin-left: 0.375rem;
}
.market-price-empty {
  font-size: 0.875rem;
  color: var(--p-text-muted-color, #6b7280);
}
.no-gateway-hint {
  color: var(--p-amber-800, #92400e);
  background: var(--p-amber-50, #fffbeb);
  border: 1px solid var(--p-amber-300, #fcd34d);
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}
</style>
